import React, { useEffect, useState } from "react";
import {
  Input,
  Tabs,
  Table,
  Tag,
  Select,
  Button,
  message,
} from "antd";
import { CustomerServiceOutlined } from "@ant-design/icons";
import {
  useGetSupportTicketsQuery,
  useGetCallbackRequestsQuery,
  useUpdateCallbackRequestMutation,
} from "../../api/authApi";
import {
  PageWrapper,
  PageHeader,
  ToolbarContainer,
  SearchInputWrapper,
  ContentWrapper,
} from "../WarehouseComponent/styles";
import TicketDetail from "./TicketDetail";

const STATUS_COLOR = {
  OPEN: "blue",
  IN_PROGRESS: "orange",
  WAITING_ON_CUSTOMER: "gold",
  RESOLVED: "green",
  CLOSED: "default",
  PENDING: "blue",
  SCHEDULED: "orange",
  COMPLETED: "green",
  CANCELLED: "default",
};

const TicketsTab = ({ onView }) => {
  const [searchFilter, setSearchFilter] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState(undefined);
  const [priority, setPriority] = useState(undefined);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchFilter), 500);
    return () => clearTimeout(t);
  }, [searchFilter]);

  const { data, isLoading } = useGetSupportTicketsQuery({
    page,
    limit: 10,
    searchFilter: debounced || undefined,
    status,
    priority,
  });

  const columns = [
    {
      title: "Reference",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
    },
    {
      title: "Submitter",
      key: "submitter",
      render: (_, r) => (
        <div>
          <div>{r.submitterName}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.submitterEmail}</div>
        </div>
      ),
    },
    { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (p) => <Tag>{p}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => new Date(d).toLocaleString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Button type="link" onClick={() => onView(r.id)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      <ToolbarContainer>
        <SearchInputWrapper>
          <Input
            placeholder="Search reference, email, name…"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            allowClear
            size="large"
          />
        </SearchInputWrapper>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 180 }}
          value={status}
          onChange={setStatus}
          options={[
            "OPEN",
            "IN_PROGRESS",
            "WAITING_ON_CUSTOMER",
            "RESOLVED",
            "CLOSED",
          ].map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder="Priority"
          style={{ width: 140 }}
          value={priority}
          onChange={setPriority}
          options={["Low", "Medium", "High", "Urgent"].map((p) => ({
            value: p,
            label: p,
          }))}
        />
      </ToolbarContainer>
      <ContentWrapper>
        <Table
          loading={isLoading}
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: 10,
            total: data?.meta?.total || 0,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </ContentWrapper>
    </>
  );
};

const CallbacksTab = () => {
  const [searchFilter, setSearchFilter] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState(undefined);
  const [page, setPage] = useState(1);
  const [updateCallback] = useUpdateCallbackRequestMutation();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchFilter), 500);
    return () => clearTimeout(t);
  }, [searchFilter]);

  const { data, isLoading } = useGetCallbackRequestsQuery({
    page,
    limit: 10,
    searchFilter: debounced || undefined,
    status,
  });

  const handleStatus = async (id, next) => {
    try {
      await updateCallback({ id, status: next }).unwrap();
      message.success("Callback updated");
    } catch {
      message.error("Failed to update callback");
    }
  };

  const columns = [
    { title: "Reference", dataIndex: "referenceNumber", key: "ref" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    {
      title: "Timing",
      key: "timing",
      render: (_, r) =>
        r.timing === "SCHEDULED"
          ? `Scheduled · ${r.scheduledSlot || "—"}`
          : "ASAP",
    },
    { title: "Topic", dataIndex: "topic", key: "topic", render: (t) => t || "—" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s, r) => (
        <Select
          value={s}
          style={{ width: 140 }}
          onChange={(v) => handleStatus(r.id, v)}
          options={["PENDING", "SCHEDULED", "COMPLETED", "CANCELLED"].map(
            (x) => ({ value: x, label: x })
          )}
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => new Date(d).toLocaleString(),
    },
  ];

  return (
    <>
      <ToolbarContainer>
        <SearchInputWrapper>
          <Input
            placeholder="Search phone, reference, topic…"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            allowClear
            size="large"
          />
        </SearchInputWrapper>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 160 }}
          value={status}
          onChange={setStatus}
          options={["PENDING", "SCHEDULED", "COMPLETED", "CANCELLED"].map(
            (s) => ({ value: s, label: s })
          )}
        />
      </ToolbarContainer>
      <ContentWrapper>
        <Table
          loading={isLoading}
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: 10,
            total: data?.meta?.total || 0,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </ContentWrapper>
    </>
  );
};

const SupportComponent = () => {
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  if (selectedTicketId) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <TicketDetail
            ticketId={selectedTicketId}
            onBack={() => setSelectedTicketId(null)}
          />
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <h1>
          <CustomerServiceOutlined style={{ marginRight: 10 }} />
          Support
        </h1>
      </PageHeader>
      <Tabs
        items={[
          {
            key: "tickets",
            label: "Tickets",
            children: <TicketsTab onView={setSelectedTicketId} />,
          },
          {
            key: "callbacks",
            label: "Callbacks",
            children: <CallbacksTab />,
          },
        ]}
      />
    </PageWrapper>
  );
};

export default SupportComponent;
